import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  SimpleGrid,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiClient from "../../../utils/ApiClient/apiClientAxios";
import CustomModal from "../../Common/CustomModal/CustomModal";
import MergeDuplication from "./MergeDuplication";
import ProfileCard from "./ProfileCard";

// Helper function to generate consistent colors
const getColorFromString = (str) => {
  if (!str) return "#718096";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }
  const colors = [
    "#E53E3E",
    "#DD6B20",
    "#D69E2E",
    "#38A169",
    "#319795",
    "#3182CE",
    "#5A67D8",
    "#805AD5",
    "#D53F8C",
    "#C05621",
    "#2C7A7B",
    "#2B6CB0",
    "#6B46C1",
    "#B83280",
    "#9B2C2C",
    "#744210",
    "#276749",
    "#234E52",
  ];
  return colors[Math.abs(hash) % colors.length];
};

const cleanPersonData = (personData) => {
  const {
    ADDRESS,
    HISTORY,
    previewAddress,
    Type,
    avatarColor,
    ...cleanedData
  } = personData;

  if (previewAddress) {
    const addresses = Array.isArray(previewAddress)
      ? previewAddress
      : Array.from(previewAddress.values());
    cleanedData["ADDRESS"] = addresses.map((addr) => {
      const { sourceName, sourcePersonId, avatarColor, ...cleanAddr } = addr;
      return cleanAddr;
    });
  }

  return cleanedData;
};

const DuplicationData = ({ records }) => {
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [mergedData, setMergedData] = useState(() => ({
    ...records[0],
    ADDRESS: records[0]?.ADDRESS || [],
    previewAddress: new Map(),
    avatarColor: getColorFromString(
      records[0]?.PERSON_ID ||
        `${records[0]?.FIRST_NAME}${records[0]?.LAST_NAME}`
    ),
  }));

  const [isLoading, setIsLoading] = useState(false);
  const [highlighted, setHighlighted] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  const {
    onOpen: onMergeDuplicate,
    isOpen: isMergeDuplicateOpen,
    onClose: onMergeDuplicateClose,
  } = useDisclosure();
  const navigate = useNavigate();

  const handleMerge = async () => {
    setIsLoading(true);
    try {
      const mergedPerson = cleanPersonData(mergedData);
      const ids = selectedProfiles.map((record) => record?.id);
      const dataToPass = {
        PERSON: mergedPerson,
        MERGES: ids,
      };
      await apiClient.post("/mergePersons", dataToPass);
      setIsLoading(false);
      onMergeDuplicateClose();
      toast.success("Person merged successfully.");
      navigate(-1);
    } catch (error) {
      setIsLoading(false);
      console.error("Merge Error:", error);
    }
  };

  // Initialize first record with color
  useEffect(() => {
    if (records.length > 0) {
      const firstRecordColor = getColorFromString(
        records[0].PERSON_ID ||
          `${records[0].FIRST_NAME}${records[0].LAST_NAME}`
      );
      setSelectedProfiles([{ ...records[0], avatarColor: firstRecordColor }]);
    }
  }, [records]);

  // Update addresses with color when selection changes
  useEffect(() => {
    if (selectedProfiles.length > 0) {
      const selectedAddresses = selectedProfiles.flatMap((rec) =>
        (rec.ADDRESS || []).map((addr) => ({
          ...addr,
          sourceName: `${rec.FIRST_NAME || ""} ${rec.LAST_NAME || ""}`.trim(),
          sourcePersonId: rec.PERSON_ID,
          avatarColor: rec.avatarColor,
        }))
      );
      setMergedData((prev) => ({
        ...prev,
        ADDRESS: selectedAddresses,
      }));
    }
  }, [selectedProfiles]);

  const handleSelectionChange = (profile, isChecked) => {
    setSelectedProfiles((prevSelected) => {
      if (profile.PERSON_ID === records[0].PERSON_ID) {
        return prevSelected;
      }
      const updatedSelection = isChecked
        ? [...prevSelected, profile]
        : prevSelected.filter((p) => p.PERSON_ID !== profile.PERSON_ID);

      const allSelected = updatedSelection.length === records.length;
      setSelectAll(allSelected);

      return updatedSelection;
    });
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);

    if (checked) {
      const recordsWithColors = records.map((rec) => ({
        ...rec,
        avatarColor: getColorFromString(
          rec.PERSON_ID || `${rec.FIRST_NAME}${rec.LAST_NAME}`
        ),
      }));
      setSelectedProfiles(recordsWithColors);
    } else {
      const firstRecordColor = getColorFromString(
        records[0].PERSON_ID ||
          `${records[0].FIRST_NAME}${records[0].LAST_NAME}`
      );
      setSelectedProfiles([{ ...records[0], avatarColor: firstRecordColor }]);
    }
  };

  const handleSeparate = () => {
    setIsLoading(true);
    const personIds = selectedProfiles.map((record) => record.PERSON_ID);
    const dataToPass = { PERSONS: personIds };

    fetch("/api/separatePerson", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dataToPass),
    })
      .then((res) => {
        if (res.ok) {
          toast.success("Person separated successfully.");
          navigate("/admin/duplicate");
        } else {
          toast.error("Failed to separate person. Please try again.");
        }
      })
      .catch((error) => {
        console.error("Separation Error:", error);
        toast.error("Something went wrong. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      <Flex alignItems="center" gap={2} justify="space-between" mb={1}>
        <Box>
          <Button
            onClick={() => navigate(-1)}
            size="sm"
            leftIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
        <Box>
          <Button
            disabled={selectedProfiles?.length < 1}
            size="sm"
            mr={2}
            onClick={handleSeparate}
          >
            Keep Separate
          </Button>
          <Button
            disabled={selectedProfiles?.length < 2}
            size="sm"
            mr={2}
            onClick={onMergeDuplicate}
          >
            Merge
          </Button>
        </Box>
      </Flex>

      <Box my={2}>
        <Flex alignItems="center" mb={2}>
          <Text fontSize="xl" fontWeight={500} mr={4}>
            Basic Information
          </Text>
          <Checkbox isChecked={selectAll} onChange={handleSelectAll}>
            Select All
          </Checkbox>
        </Flex>
        <SimpleGrid columns={4} spacing={4}>
          {records?.map((item, index) => (
            <Box key={item.PERSON_ID}>
              <ProfileCard
                item={item}
                onSelectionChange={handleSelectionChange}
                isSelected={selectedProfiles.some(
                  (p) => p.PERSON_ID === item.PERSON_ID
                )}
                isFirst={index === 0}
              />
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      <CustomModal
        size="6xl"
        isCentered
        title="Merge duplicate"
        isOpen={isMergeDuplicateOpen}
        onClose={onMergeDuplicateClose}
        bodyContent={
          <MergeDuplication
            records={selectedProfiles}
            setMergedData={setMergedData}
            mergedData={mergedData}
            setHighlighted={setHighlighted}
            highlighted={highlighted}
            selectedProfilesWithColors={selectedProfiles}
          />
        }
        footerContent={
          <Button
            variant="primary"
            w="200px"
            mt={5}
            onClick={handleMerge}
            disabled={mergedData?.previewAddress?.size === 0}
          >
            {isLoading ? "Loading..." : "Submit"}
          </Button>
        }
      />
    </>
  );
};

export default DuplicationData;
